/**
 * Utilitaires Solana côté Convex — validateur d'adresse wallet.
 * Feature: usdc-payroll
 *
 * Le validateur est un helper PUR exporté ici pour être réutilisé
 * par la mutation `members.setWalletAddress`, par l'éditeur d'adresse côté
 * front (`WalletAddressEditor.tsx`) et par les tests unitaires.
 * Convex ne touche jamais la blockchain — il reçoit uniquement le txAddress
 * confirmé et valide le format des adresses avant stockage.
 *
 * 🩸 « base58 de 32 à 44 caractères » ne suffit PAS, et ça a coûté 22,95 $ non
 * versés et 19 échecs muets entre le 25 et le 31-08-2026. Une adresse TRON
 * (USDT TRC-20) fait 34 caractères, tous dans l'alphabet base58 : elle passait
 * ce contrôle, se rangeait en base, et mourait bien plus loin — côté backend,
 * au moment de signer, sur un « String is the wrong size ». L'employé voyait
 * « erreur inattendue » et recliquait.
 *
 * Ce qui tranche, c'est le DÉCODAGE : une clé publique Solana fait exactement
 * 32 octets, une adresse TRON 25 (0x41 + 20 + 4 de checksum). La longueur en
 * caractères est une conséquence, pas la règle.
 *
 * 🪤 CROSS-REF : `app/domain/services/payroll/solana_address.py` (dépôt
 * Francis). Ce module et lui DOIVENT dire la même chose — ici c'est le seul
 * chemin d'écriture, là-bas le dernier filet avant la signature.
 */

/**
 * Alphabet base58 Solana (Bitcoin-style, sans '0', 'O', 'I', 'l').
 * Req 1.2, 1.3 — Property 1.
 */
const BASE58_REGEX = /^[1-9A-HJ-NP-Za-km-z]+$/;
const BASE58_ALPHABET =
  "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

/** Taille d'une clé publique Solana (ed25519), en octets. C'est LA règle. */
export const SOLANA_ADDRESS_BYTES = 32;

/** Taille d'une adresse TRON (base58check) : 0x41 + 20 + 4 de checksum. */
export const TRON_ADDRESS_BYTES = 25;
/** Premier octet d'une adresse TRON mainnet — c'est lui qui donne le « T ». */
export const TRON_ADDRESS_PREFIX_BYTE = 0x41;

/**
 * Décode du base58 (sans checksum). Rend `null` si un caractère est hors
 * alphabet. Volontairement autonome — aucune dépendance dans `convex/_lib`.
 */
function base58Decode(address: string): Uint8Array | null {
  const octets: number[] = [];
  for (const caractere of address) {
    let report = BASE58_ALPHABET.indexOf(caractere);
    if (report < 0) return null;
    for (let i = 0; i < octets.length; i++) {
      report += octets[i] * 58;
      octets[i] = report & 0xff;
      report >>= 8;
    }
    while (report > 0) {
      octets.push(report & 0xff);
      report >>= 8;
    }
  }
  // Un '1' de tête code un octet nul de tête — il ne ressort pas du calcul.
  let zeros = 0;
  while (zeros < address.length && address[zeros] === "1") zeros++;
  return new Uint8Array([...new Array<number>(zeros).fill(0), ...octets.reverse()]);
}

/**
 * `true` si l'adresse est une adresse TRON (USDT TRC-20). Sert uniquement à
 * dire POURQUOI on refuse : c'est la confusion réelle des employés, qui ont
 * tous une adresse USDT TRON avant d'en avoir une Solana.
 */
export function isTronAddress(address: string): boolean {
  if (typeof address !== "string") return false;
  if (!BASE58_REGEX.test(address)) return false;
  const octets = base58Decode(address);
  return (
    octets !== null &&
    octets.length === TRON_ADDRESS_BYTES &&
    octets[0] === TRON_ADDRESS_PREFIX_BYTE
  );
}

/**
 * Valide qu'une chaîne est une adresse Solana valide :
 *   - Longueur comprise entre 32 et 44 caractères inclus.
 *   - Caractères exclusivement base58 ([1-9A-HJ-NP-Za-km-z]).
 *   - Et surtout : décode sur exactement 32 octets.
 *
 * On ne vérifie PAS on-chain que l'adresse existe ou possède un ATA USDC —
 * trop coûteux et inutilement bloquant pour l'employé (cf design.md §3).
 *
 * @returns `true` si valide, `false` sinon.
 */
export function validateSolanaAddress(address: string): boolean {
  if (typeof address !== "string") return false;
  if (address.length < 32 || address.length > 44) return false;
  if (!BASE58_REGEX.test(address)) return false;
  const octets = base58Decode(address);
  return octets !== null && octets.length === SOLANA_ADDRESS_BYTES;
}

/**
 * Code d'erreur Convex retourné par `members.setWalletAddress` quand
 * l'adresse soumise ne passe pas la validation.
 */
export const INVALID_WALLET_ADDRESS = "INVALID_WALLET_ADDRESS" as const;

/**
 * Cas particulier d'`INVALID_WALLET_ADDRESS` : l'adresse est une adresse TRON.
 * Distingué pour que l'UI puisse dire « c'est ton adresse USDT TRON, il faut
 * ton adresse Solana » plutôt qu'un « format invalide » que personne ne sait
 * corriger.
 */
export const WALLET_ADDRESS_IS_TRON = "WALLET_ADDRESS_IS_TRON" as const;
